// src/components/common/SectionHeader.tsx
//
// Standard horizontal header for every section's content area.
// One container for all screens: a subtle card (paper surface, 1px divider
// border, soft shadow, medium radius) that floats over the app background and
// sticks to the top on scroll.
//
// Layout: [ left zone: tabs / inline search / scope·date context ]  ...spacer...
//         [ right cluster: Help · Filters · Refresh · | · Export · Create ]
//
// Filter rule (product decision):
//   - search-only screens pass an inline `left` search field and NO filterContent
//     → no Filters button.
//   - screens with more than one filter pass `filterContent` → a Filters button
//     appears and opens a Popover with those fields.

import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Fab,
  IconButton,
  Popover,
  Tooltip,
  Badge,
  CircularProgress,
  Divider,
  TextField,
  InputAdornment,
  alpha,
  type SxProps,
  type Theme,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Add as AddIcon,
  FileDownload as ExportIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { visuallyHidden } from '@mui/utils';
import { useNotify, useTranslate } from 'react-admin';
import { useLocation } from 'react-router-dom';
import { Typography } from '@mui/material';
import { PageHelp, type PageHelpConfig } from './PageHelp';
import { useFilterPopover } from './RaFilterPopover';
import { resourcesConfig } from '@/config/resourceConfig';
import { ManualHelpButton } from '@/features/manual/components/ManualHelpButton';

/** Resolve the current section's title key from the route (3c hybrid: the
 *  section header carries the page title). Screens can override via `title`. */
const useSectionTitle = (): string | null => {
  const location = useLocation();
  const resource = Object.values(resourcesConfig).find(r =>
    r.route === '/' ? location.pathname === '/' : location.pathname === r.route || location.pathname.startsWith(`${r.route}/`)
  );
  if (!resource) return null;
  return resource.name === 'dashboard' ? 'ra.page.dashboard' : resource.label;
};

// Compact sizing for the toolbar buttons. v3: no pill radius — buttons keep the
// theme's rounded (radius.medium) corners; colour comes from the button variant
// (outlined = secondary white, contained = teal-ink), never hardcoded here.
export const actionButtonSx = {
  fontSize: '0.75rem',
  py: 0.4,
  px: 1.75,
  minHeight: '32px',
  lineHeight: 1.2,
  textTransform: 'none' as const,
  fontWeight: 600,
  whiteSpace: 'nowrap' as const,
  '& .MuiButton-startIcon': {
    marginRight: '4px',
    '& > *:nth-of-type(1)': { fontSize: '17px' },
  },
};

export interface SectionHeaderProps {
  /** Page title (h1). Defaults to the current resource's name; pass `null` to hide. */
  title?: string | null;
  /** Keep the h1 for screen readers but do not draw it. Default: hidden when the title is the
   *  one resolved from the route (it repeats the rail's section tab), drawn when passed in. */
  hideTitle?: boolean;
  /** Optional subtitle rendered in tertiary text under the title. */
  subtitle?: string;
  /** Contextual help launcher, rendered first in the right cluster. */
  helpConfig?: PageHelpConfig;
  /** Screen key of the user manual (roommatik.analytics `ManualScreens.Known`): shows «Ver en el
   *  manual» beside the quick help when a section of the reader's edition explains this screen. */
  manualScreen?: string;
  /** Left zone: tabs (SubMenu), inline search field, scope/date controls, etc. */
  left?: React.ReactNode;
  /** Filter fields rendered inside the popover. When provided, a Filters button appears. */
  filterContent?: React.ReactNode;
  /** Drive the Filters button from the RA FilterPopover context (for React-Admin lists). */
  useRaFilters?: boolean;
  /** Show an inline search field (RA mode) bound to the list's search filter. */
  showSearch?: boolean;
  /** Placeholder for the inline search field. */
  searchPlaceholder?: string;
  /** Number of active filters — drives the filled state and the count badge. */
  activeFilterCount?: number;
  /** Controlled popover open state (optional). */
  filtersOpen?: boolean;
  onFiltersOpenChange?: (open: boolean) => void;
  /** Refresh icon button. */
  onRefresh?: () => void;
  refreshing?: boolean;
  /**
   * Export button. When the handler returns a promise the button shows a busy
   * state («Exportando…», disabled) until it settles, and a failure is
   * reported to the user instead of passing silently.
   */
  showExport?: boolean;
  onExportClick?: () => void | Promise<unknown>;
  /** Create button (desktop) + floating action button (mobile). */
  showCreate?: boolean;
  onCreateClick?: () => void;
  /** Extra actions placed before the Export/Create divider (metadata toggle, bulk delete…). */
  extraActions?: React.ReactNode;
  /** Fully custom actions appended at the end of the right cluster (theme/fullscreen…). */
  rightActions?: React.ReactNode;
  /** Stick the bar to the top on scroll (default true). */
  sticky?: boolean;
  /** Extra styles for the bar (e.g. horizontal inset to line up with padded page content). */
  sx?: SxProps<Theme>;
  /** Max width of the filters popover. */
  filterPopoverMaxWidth?: number;
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  hideTitle,
  subtitle,
  helpConfig,
  manualScreen,
  left,
  filterContent,
  useRaFilters,
  showSearch,
  searchPlaceholder,
  activeFilterCount = 0,
  filtersOpen,
  onFiltersOpenChange,
  onRefresh,
  refreshing,
  showExport,
  onExportClick,
  showCreate,
  onCreateClick,
  extraActions,
  rightActions,
  sticky = true,
  sx,
  filterPopoverMaxWidth = 660,
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [exporting, setExporting] = useState(false);
  const handleExportClick = async () => {
    if (!onExportClick || exporting) return;
    setExporting(true);
    try {
      await onExportClick();
    } catch {
      notify('common.export.failed', { type: 'error' });
    } finally {
      setExporting(false);
    }
  };
  const exportLabel = translate(exporting ? 'common.export.inProgress' : 'ra.action.export');

  const raCtx = useFilterPopover();
  const raMode = !!useRaFilters && !!raCtx;

  const filterBtnRef = useRef<HTMLButtonElement | null>(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = filtersOpen !== undefined;

  const open = raMode ? raCtx!.open : (isControlled ? !!filtersOpen : internalOpen);
  const setOpen = (next: boolean) => {
    if (raMode) raCtx!.setOpen(next);
    else if (isControlled) onFiltersOpenChange?.(next);
    else setInternalOpen(next);
  };

  // Register the button as the popover anchor when in RA mode.
  const setFilterBtn = (node: HTMLButtonElement | null) => {
    filterBtnRef.current = node;
    if (raMode && node) raCtx!.setAnchorEl(node);
  };

  const hasFilters = raMode ? raCtx!.hasFilters : Boolean(filterContent);
  const activeCount = raMode ? raCtx!.activeCount : activeFilterCount;
  const filtersActive = activeCount > 0;
  const showDivider = (showExport && onExportClick) || (!isMobile && showCreate && onCreateClick);

  // In RA/self-managed mode the active content (e.g. a Logs tab) may register its
  // own Refresh action via the context; fall back to it when no prop is provided.
  const effectiveRefresh = onRefresh ?? (raMode ? raCtx!.refreshFn : undefined);
  const effectiveRefreshing = refreshing ?? (raMode ? raCtx!.refreshing : undefined);

  // 3c hybrid: the section header carries the page title (h1 + tertiary subtitle).
  const autoTitleKey = useSectionTitle();
  const resolvedTitle = title !== undefined ? title : (autoTitleKey ? translate(autoTitleKey, { smart_count: 2 }) : null);
  // The route's own title repeats the selected section tab: keep it for assistive tech only.
  const titleHidden = hideTitle ?? (title === undefined);

  return (
    <>
      <Box
        sx={[
        {
          display: 'flex',
          alignItems: 'center',
          columnGap: 1,
          rowGap: 0.75,
          flexWrap: 'wrap',
          px: 0.5,
          py: 0.25,
          mb: 1.5,
          minHeight: 36,
          // One control height for every action in the bar: the theme's 38px buttons and
          // the scope/segmented controls drop to the 32px toolbar size.
          '& .MuiButton-root': { minHeight: 32, py: 0.4 },
          '& .MuiToggleButton-root': { height: 32, py: 0, lineHeight: 1.2 },
          '& .MuiFormControlLabel-root': { minHeight: 32 },
          // Fixed header row: don't shrink when it's a flex child of the page
          // column (Pattern A) — the list below takes the remaining height.
          flexShrink: 0,
          // v3.6: the section header sits flat on the warm canvas (opaque so the
          // sticky bar covers the scrolling content), NOT inside a white card —
          // only the table below is a card.
          backgroundColor: 'background.default',
          // Sticky from 600px up only: at phone width the wrapped filter rows
          // would pin close to half the screen over the content.
          ...(sticky && {
            position: { xs: 'static', sm: 'sticky' },
            top: 0,
            zIndex: 3,
          }),
        },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {/* Title (h1, page-title size) + optional tertiary subtitle */}
        {resolvedTitle && (titleHidden ? (
          <>
            <Box component="h1" sx={visuallyHidden}>{resolvedTitle}</Box>
            {/* The subtitle explains the screen: keep it, as a quiet line under the controls. */}
            {subtitle && (
              <Typography sx={{ order: 99, flexBasis: '100%', fontSize: '0.8125rem', lineHeight: 1.35, color: theme.custom?.text?.tertiary }}>
                {subtitle}
              </Typography>
            )}
          </>
        ) : (
          <Box sx={{ mr: 1, minWidth: 0 }}>
            <Typography
              variant="h1"
              component="h1"
              noWrap
              sx={{ color: 'text.primary', fontSize: '1.25rem', lineHeight: 1.3 }}
            >
              {resolvedTitle}
            </Typography>
            {subtitle && (
              <Typography noWrap sx={{ fontSize: '0.8125rem', lineHeight: 1.35, color: theme.custom?.text?.tertiary }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        ))}

        {/* Left zone */}
        {left && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
            {left}
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 8 }} />

        {/* Right cluster */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end', minWidth: 0 }}>
          {helpConfig && <PageHelp config={helpConfig} />}
          {manualScreen && <ManualHelpButton screen={manualScreen} />}

          {/* Inline search (search-only screens) — same slot as the Filters button */}
          {raMode && showSearch && (
            <TextField
              size="small"
              value={raCtx!.searchValue}
              onChange={(e) => raCtx!.setSearchValue(e.target.value)}
              placeholder={searchPlaceholder ?? translate('ra.action.search')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className="SectionHeader-searchIcon" sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: { xs: '100%', sm: 220 },
                '& .MuiOutlinedInput-root': {
                  height: 32,
                  borderRadius: 999,
                  backgroundColor: 'background.paper',
                  fontSize: '0.8125rem',
                },
              }}
            />
          )}

          {/* Filters — only when the screen has more than just a search field */}
          {hasFilters && (
            isMobile ? (
              <IconButton
                ref={setFilterBtn}
                onClick={() => setOpen(!open)}
                size="small"
                sx={{
                  color: open || filtersActive ? 'white' : 'text.secondary',
                  backgroundColor: open || filtersActive ? theme.custom.ctaFill : 'transparent',
                  border: '1px solid',
                  borderColor: open || filtersActive ? theme.custom.ctaFill : alpha(theme.palette.text.secondary, 0.3),
                  '&:hover': {
                    backgroundColor: open || filtersActive ? theme.custom.ctaFillHover : alpha(theme.palette.primary.main, 0.04),
                  },
                }}
              >
                <Badge badgeContent={activeCount} color="primary" overlap="circular">
                  <FilterListIcon sx={{ fontSize: 18 }} />
                </Badge>
              </IconButton>
            ) : (
              <Button
                ref={setFilterBtn}
                onClick={() => setOpen(!open)}
                size="small"
                // v3 single button system: outlined (white) at rest, contained
                // (teal-ink) when open or with active filters. No warm pill.
                variant={open || filtersActive ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon sx={{ fontSize: '16px !important' }} />}
                sx={{
                  fontSize: '0.75rem',
                  py: 0.4,
                  px: 1.75,
                  minHeight: '32px',
                  lineHeight: 1.2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                {translate('common.filters.label')}
                {filtersActive && (
                  <Box
                    component="span"
                    sx={{
                      ml: 0.75,
                      px: 0.6,
                      minWidth: 18,
                      height: 18,
                      borderRadius: 999,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'white',
                      color: theme.custom.ctaFill,
                    }}
                  >
                    {activeCount}
                  </Box>
                )}
              </Button>
            )
          )}

          {/* Refresh */}
          {effectiveRefresh && (
            <Tooltip title={translate('ra.action.refresh')}>
              <span>
                <IconButton
                  onClick={effectiveRefresh}
                  disabled={effectiveRefreshing}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    borderRadius: theme.custom?.radius?.small ?? '8px',
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08), color: 'primary.dark' },
                  }}
                >
                  {effectiveRefreshing ? <CircularProgress size={18} /> : <RefreshIcon sx={{ fontSize: 20 }} />}
                </IconButton>
              </span>
            </Tooltip>
          )}

          {extraActions}

          {showDivider && (
            <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />
          )}

          {/* Export */}
          {showExport && onExportClick && (
            isMobile ? (
              <Tooltip title={exportLabel}>
                <span>
                  <IconButton onClick={handleExportClick} disabled={exporting} aria-busy={exporting} aria-label={exportLabel} size="small" sx={{ color: 'primary.main' }}>
                    {exporting ? <CircularProgress size={18} color="inherit" /> : <ExportIcon sx={{ fontSize: 20 }} />}
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Button
                onClick={handleExportClick}
                disabled={exporting}
                aria-busy={exporting}
                size="small"
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <ExportIcon />}
                sx={actionButtonSx}
              >
                {exportLabel}
              </Button>
            )
          )}

          {/* Create — desktop button (mobile uses FAB below). v3: the screen's
              primary action is the teal-ink contained button (single button system). */}
          {!isMobile && showCreate && onCreateClick && (
            <Button
              onClick={onCreateClick}
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              sx={actionButtonSx}
            >
              {translate('ra.action.create')}
            </Button>
          )}

          {rightActions}
        </Box>
      </Box>

      {/* Filters popover (self-managed mode only; RA mode uses RaFilterPopover) */}
      {Boolean(filterContent) && !raMode && (
        <Popover
          open={open}
          anchorEl={filterBtnRef.current}
          onClose={() => setOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                p: 2,
                borderRadius: theme.custom?.radius?.card ?? '10px',
                maxWidth: filterPopoverMaxWidth,
                boxShadow: theme.custom?.shadows?.medium,
              },
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.25,
              alignItems: 'center',
              '& .MuiInputBase-root': {
                minHeight: '36px !important',
                height: 36,
                fontSize: '0.8125rem',
                backgroundColor: 'background.paper',
                borderRadius: theme.custom?.radius?.small ?? '8px',
              },
              '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
              '& .MuiFormControl-root': { m: 0 },
              '& .MuiFormHelperText-root': { display: 'none' },
            }}
          >
            {filterContent}
          </Box>
        </Popover>
      )}

      {/* Mobile FAB for Create — same teal-ink CTA as the desktop primary action */}
      {isMobile && showCreate && onCreateClick && (
        <Fab
          size="medium"
          onClick={onCreateClick}
          sx={{
            position: 'fixed',
            right: 16,
            bottom: 24,
            zIndex: 1000,
            color: 'white',
            backgroundColor: theme.custom.ctaFill,
            boxShadow: `0 6px 16px -6px ${alpha(theme.custom.ctaFill, 0.7)}`,
            '&:hover': { backgroundColor: theme.custom.ctaFillHover },
          }}
        >
          <AddIcon />
        </Fab>
      )}
    </>
  );
};

export default SectionHeader;
